import { Injectable, Injector } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { SharedService } from '../../shared/services/shared.service';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {

    // This method is used to initialize the class instance using injector and router
    constructor(
        private injector: Injector,
        private router: Router
    ) { }

    // This method is used to intercept using req and next
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const isOauthTokenRequest = req.url.includes('/oauth2/token');
        //Special check for send soruce cp to get liferay token in response
        if (isOauthTokenRequest) {
            const tokenRequest = req.clone({
                setHeaders: {
                    Source: 'CP'
                }
            });
            return next.handle(tokenRequest);
        }

        const oauthBypassEndpoints = ['/oauth2/token', '/.well-known/openid-configuration', '/userinfo'];
        const shouldBypassAuth = oauthBypassEndpoints.some((endpoint) => req.url.includes(endpoint));
        if (shouldBypassAuth) {
            return next.handle(req);
        }

        const shareService = this.injector.get(SharedService);
        const loginUser: any = shareService.getUser();
        const normalizedUrl = req.url.toLowerCase();
        const isVenueBookingRequest = normalizedUrl.includes('/venue/');
        const hasAuthorizationHeader = req.headers.has('Authorization');
        const venueServiceToken = isVenueBookingRequest ? this.getVenueServiceToken() : null;
        const guestToken = venueServiceToken || (loginUser ? null : this.getGuestAccessToken());
        let updatedReq = req;
        //Add guest in url for the DCCICPIntegration API only if guest token
        if (
            guestToken &&
            (req.url.includes('DCCICPIntegration/RunSiebelProcessRest/RunSiebelProcess')||
            req.url.includes('DCCaseIntegrationAPI/CaseIntegrationAPI_RESTProxy'))            
        ) {
            const modifiedUrl = req.url.replace('/v1/', '/v1/guest/');
            updatedReq = req.clone({ url: modifiedUrl });
        }

        const resolvedAuthorization = hasAuthorizationHeader
            ? req.headers.get('Authorization') || ''
            : venueServiceToken
                ? `Bearer ${venueServiceToken}`
                : loginUser
                    ? `Bearer ${loginUser?.access_token}`
                    : guestToken
                        ? `Bearer ${guestToken}`
                        : '';

        let headersConfig: { [key: string]: string } = {
            // "SSO_SIEBEL_USER": loginUser ? loginUser.user.username : '',
            "Authorization": resolvedAuthorization,
            "lang": shareService.getCurrentLang() == "ar" ? "ARA" : "ENU",
            "Source": "CP"
        };
        if (normalizedUrl.includes('venue-reservation-rest/v1.0/room-book')) {
            headersConfig['Accept-Language'] = shareService.getCurrentLang() === 'ar' ? 'ar-SA' : 'en-US';
        }
        if (updatedReq.url.includes('scim2/Me')) {
            headersConfig['Accept'] = 'application/scim+json';
            headersConfig['Content-Type'] = 'application/scim+json';
        }
        const clonedRequest = updatedReq.clone({ setHeaders: headersConfig });

        return next.handle(clonedRequest).pipe(
            catchError(err => {
                if (err.status === 401) {
                    this.router.navigate(['/unauthorized']);
                }
                // if ([400, 404, 405, 500].includes(err.status)) {
                // }
                return throwError(() => err);
            })
        );
    }

    private getGuestAccessToken(): string | null {
        try {
            const raw = localStorage.getItem('guest_access_token');
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw) as { access_token?: string; expiresAt?: number };
            if (parsed?.access_token && parsed?.expiresAt && parsed.expiresAt > Date.now()) {
                return parsed.access_token;
            }
        } catch {
            return null;
        }
        return null;
    }

    private getVenueServiceToken(): string | null {
        const token = String(localStorage.getItem('venue_service_token') ?? '').trim();
        if (!token) {
            return null;
        }

        const payload = this.decodeJwtPayload(token);
        const exp = Number(payload?.['exp'] ?? 0);
        const bufferMs = 60_000;
        if (!exp || exp * 1000 - bufferMs <= Date.now()) {
            localStorage.removeItem('venue_service_token');
            return null;
        }

        return token;
    }

    private decodeJwtPayload(token: string): Record<string, any> | null {
        const parts = String(token ?? '').split('.');
        if (parts.length < 2) {
            return null;
        }

        try {
            const normalized = parts[1]
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
            return JSON.parse(window.atob(padded));
        } catch {
            return null;
        }
    }
}
